/**
 * Koordinator App Router
 * Main routing configuration with protected routes and dashboard layout
 *
 * Code splitting: Heavy pages are lazy-loaded to reduce initial bundle size.
 * The main entry chunk stays small while feature areas load on demand.
 */
import React, { Suspense, lazy } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  Link,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { Spinner } from '@bright-web-skeleton/ui';
import { useInviteIQCapture } from '@bright-web-skeleton/shared/inviteiq';
import { useNoIndex } from '@bright-web-skeleton/shared/seo';

// Layout - loaded eagerly (needed for shell)
import { DashboardLayout } from '../components/layout';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { useAuth } from '../shared/contexts';
import { BrandStoreProvider } from '../shared/providers/BrandStoreContext';
import { BrandStoreLocaleExperienceSync } from '../shared/providers/BrandStoreLocaleExperienceSync';
import { KoordinatorI18nProvider } from '../shared/i18n/KoordinatorI18nProvider';
import { hasFreshOnboardingExit } from '../utils/onboardingExternalRedirect';

// Public Pages - loaded eagerly (landing/auth are entry points)
import { LandingPage } from '../pages/landing';
import { AdminLandingPage } from '../pages/admin';
import { SignupPage } from '../pages/signup';
import { LoginPage } from '../pages/auth';

// New Phase B pages
const InternalBrandStorePage = lazy(() =>
  import('../pages/brand/InternalBrandStorePage').then((m) => ({
    default: m.InternalBrandStorePage,
  })),
);
const CreatorDiscoveryPage = lazy(() =>
  import('../pages/creators/CreatorDiscoveryPage').then((m) => ({
    default: m.CreatorDiscoveryPage,
  })),
);
const InternalCreatorPage = lazy(() =>
  import('../pages/creators/InternalCreatorPage').then((m) => ({ default: m.InternalCreatorPage })),
);
const AuthCallbackPage = lazy(() =>
  import('../pages/core/auth/AuthCallbackPage').then((m) => ({ default: m.AuthCallbackPage })),
);

// Public marketing/support pages (linked from the landing footer).
const AboutPage = lazy(() => import('../pages/company/AboutPage'));
const CareersPage = lazy(() => import('../pages/company/CareersPage'));
const PressPage = lazy(() => import('../pages/company/PressPage'));
const ContactPage = lazy(() => import('../pages/company/ContactPage'));
const PublicPrivacyPage = lazy(() => import('../pages/company/PrivacyPage'));
const PublicTermsPage = lazy(() => import('../pages/company/TermsPage'));
const HelpCenterPage = lazy(() => import('../pages/support/HelpCenterPage'));
const ApiDocsPage = lazy(() => import('../pages/support/ApiDocsPage'));

// Tailwind sanity page
const TailwindSanityPage = lazy(() =>
  import('@bright-web-skeleton/ui').then((m) => ({ default: (m as any).TailwindSanityPage })),
);

// ----------------------
// Lazy-loaded pages for code splitting
// ----------------------

// Onboarding — single-step brand+store creation (canonical)
const CreateBrandProfilePage = lazy(() =>
  import('../pages/brand/CreateBrandProfilePage').then((m) => ({
    default: m.CreateBrandProfilePage,
  })),
);
// Phase C verification fix — canonical Profile Identity step (creates members row).
const ProfileIdentityPage = lazy(() =>
  import('../pages/core/onboarding/ProfileIdentityPage').then((m) => ({
    default: m.ProfileIdentityPage,
  })),
);
const CreateBrandWizardPage = lazy(() =>
  import('../pages/brand/CreateBrandWizardPage').then((m) => ({
    default: m.CreateBrandWizardPage,
  })),
);
const BrandHandleInvitePage = lazy(() =>
  import('../pages/brand/BrandHandleInvitePage').then((m) => ({
    default: m.BrandHandleInvitePage,
  })),
);
const ProfileBrandInvitePage = lazy(() =>
  import('../pages/profile/ProfileBrandInvitePage').then((m) => ({
    default: m.ProfileBrandInvitePage,
  })),
);
const BrandAssessmentStep = lazy(() =>
  import('../pages/onboarding').then((m) => ({ default: m.BrandAssessmentStep })),
);
const BrandOnboardingPage = lazy(() =>
  import('../pages/onboarding').then((m) => ({ default: m.BrandOnboardingPage })),
);

// Dashboard pages (lazy)
const DashboardPage = lazy(() =>
  import('../pages/dashboard').then((m) => ({ default: m.DashboardPage })),
);
const StorePage = lazy(() => import('../pages/store').then((m) => ({ default: m.StorePage })));
const StoresListPage = lazy(() =>
  import('../pages/store').then((m) => ({ default: m.StoresListPage })),
);
const BrandStoresPage = lazy(() =>
  import('../pages/brandstores').then((m) => ({ default: m.BrandStoresPage })),
);
const StoreConnectedPage = lazy(() =>
  import('../pages/store').then((m) => ({ default: m.StoreConnectedPage })),
);
const StoreSetupPage = lazy(() => import('../pages/store/StoreSetupPage'));
const StoreDashboardPage = lazy(() => import('../pages/store/StoreDashboardPage'));
const CloneStorePage = lazy(() =>
  import('../pages/store').then((m) => ({ default: m.CloneStorePage })),
);
const CreateBrandStorePage = lazy(() => import('../pages/store/CreateBrandStorePage'));

// Proposals - heavy feature area
const ProposalsPage = lazy(() =>
  import('../pages/proposals').then((m) => ({ default: m.ProposalsPage })),
);
const KatalogMatchesPage = lazy(() =>
  import('../pages/proposals/KatalogMatchesPage').then((m) => ({ default: m.KatalogMatchesPage })),
);
const ActionCenterPage = lazy(() =>
  import('../pages/actions/ActionCenterPage').then((m) => ({ default: m.ActionCenterPage })),
);
const CreateProposalPage = lazy(() =>
  import('../pages/proposals').then((m) => ({ default: m.CreateProposalPage })),
);
const ProposalDetailPage = lazy(() =>
  import('../pages/proposals').then((m) => ({ default: m.ProposalDetailPage })),
);
const EditProposalPage = lazy(() =>
  import('../pages/proposals').then((m) => ({ default: m.EditProposalPage })),
);

// Native Products
const ProductsPage = lazy(() =>
  import('../pages/products').then((m) => ({ default: m.ProductsPage })),
);
const ProductEditorPage = lazy(() =>
  import('../pages/products').then((m) => ({ default: m.ProductEditorPage })),
);

// Orders & Fulfillment
const OrdersPage = lazy(() => import('../pages/orders').then((m) => ({ default: m.OrdersPage })));
const BrandOrderDetailPage = lazy(() =>
  import('../pages/orders/OrderDetailPage').then((m) => ({ default: m.OrderDetailPage })),
);
const FulfillmentPage = lazy(() =>
  import('../pages/fulfillment').then((m) => ({ default: m.FulfillmentPage })),
);

// Analytics
const AnalyticsPage = lazy(() =>
  import('../pages/analytics').then((m) => ({ default: m.AnalyticsPage })),
);

// Navbar pages
const DiscoverPage = lazy(() =>
  import('../pages/discover').then((m) => ({ default: m.DiscoverPage })),
);
const NotificationsPage = lazy(() =>
  import('../pages/notifications').then((m) => ({ default: m.NotificationsPage })),
);
const HomePage = lazy(() => import('../pages/home').then((m) => ({ default: m.HomePage })));

// Account pages - lazy loaded as a group
const BrandAccountPage = lazy(() =>
  import('../pages/account').then((m) => ({ default: m.BrandAccountPage })),
);
const BrandAgentPage = lazy(() =>
  import('../pages/account').then((m) => ({ default: m.BrandAgentPage })),
);
const BrandInfoPage = lazy(() =>
  import('../pages/account').then((m) => ({ default: m.BrandInfoPage })),
);
const WalletPage = lazy(() => import('../pages/account').then((m) => ({ default: m.WalletPage })));
const WithdrawalStatementPage = lazy(() =>
  import('../pages/account').then((m) => ({ default: m.WithdrawalStatementPage })),
);
const WalletBalancePage = lazy(() =>
  import('../pages/account').then((m) => ({ default: m.WalletBalancePage })),
);
const BrandStorePage = lazy(() =>
  import('../pages/account').then((m) => ({ default: m.BrandStorePage })),
);
const InvitePage = lazy(() => import('../pages/account').then((m) => ({ default: m.InvitePage })));
const TeamMembersPage = lazy(() =>
  import('../pages/account').then((m) => ({ default: m.TeamMembersPage })),
);
const VerifyBrandPage = lazy(() =>
  import('../pages/account').then((m) => ({ default: m.VerifyBrandPage })),
);
const VerifyOwnerPage = lazy(() =>
  import('../pages/account').then((m) => ({ default: m.VerifyOwnerPage })),
);
const TermsPage = lazy(() => import('../pages/account').then((m) => ({ default: m.TermsPage })));
const AddFundsPage = lazy(() =>
  import('../pages/account').then((m) => ({ default: m.AddFundsPage })),
);
const BrandAgentApplyPage = lazy(() =>
  import('../pages/account').then((m) => ({ default: m.BrandAgentApplyPage })),
);
const AdministrationPage = lazy(() =>
  import('../pages/account').then((m) => ({ default: m.AdministrationPage })),
);
const ManageBrandsPage = lazy(() =>
  import('../pages/account').then((m) => ({ default: m.ManageBrandsPage })),
);

// Settings
const SettingsPage = lazy(() =>
  import('../pages/settings').then((m) => ({ default: m.SettingsPage })),
);

// Kloutforce Info
const KloutforceInfoPage = lazy(() =>
  import('../pages/kloutforce').then((m) => ({ default: m.KloutforceInfoPage })),
);

// Brand Instructions
const BrandInstructionsPage = lazy(() =>
  import('../pages/BrandInstructionsPage').then((m) => ({ default: m.BrandInstructionsPage })),
);

// Operator invite landing page
const OperatorInvitePage = lazy(() =>
  import('../pages/OperatorInvitePage').then((m) => ({ default: m.OperatorInvitePage })),
);

// ----------------------
// Suspense wrapper for lazy components
// ----------------------
const LazyPage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense
    fallback={
      <div className="min-h-[50vh] flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    }
  >
    {children}
  </Suspense>
);

// ----------------------
// 404 fallback
// ----------------------
const NotFoundPage: React.FC = () => {
  const { user, loading } = useAuth();

  // If not logged in, redirect to landing instead of showing 404
  if (!loading && !user) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-foreground mb-2">404</h1>
        <p className="text-muted-foreground mb-4">Page not found</p>
        <Link to="/command-center" className="text-primary hover:underline">
          Return to Command Center
        </Link>
      </div>
    </div>
  );
};

// ----------------------
// Root redirect based on auth state
// ----------------------
const RootRedirect: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Spinner size="lg" />
      </div>
    );
  }

  // Not logged in - show landing page
  if (!user) return <LandingPage />;

  // Logged in but not onboarded - redirect to new onboarding flow,
  // unless the user has explicitly exited onboarding recently. Honoring
  // the exit flag prevents a permanent loop when an incomplete brand
  // chooses Skip / Go to dashboard / Command Center.
  if (!user.onboarded && !hasFreshOnboardingExit(user.id)) {
    return <Navigate to="/onboarding/brand" replace />;
  }

  return <Navigate to="/command-center" replace />;
};

// ----------------------
// Root layout wrapper (minimal, no sidebar)
// ----------------------
const RootLayout: React.FC = () => {
  // Capture ?iq= from URL on any route entry
  useInviteIQCapture();
  // Internal app — block search engines
  useNoIndex();

  return (
    <div className="min-h-screen bg-background text-foreground" data-theme="koordinator">
      <Outlet />
    </div>
  );
};

// ----------------------
// Redirect /account/brand-store → /account/:handle (resolve handle dynamically)
// ----------------------
const BrandStoreRedirect: React.FC = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [resolved, setResolved] = React.useState(false);

  React.useEffect(() => {
    if (!user?.id) return;
    (async () => {
      const { resolveBrandForUser } = await import('../lib/resolveBrand');
      const result = await resolveBrandForUser(user.id);
      if (!result?.brandId) {
        nav('/account/manage', { replace: true });
        return;
      }

      const { data } = await (await import('../lib/supabase')).supabase
        .from('brands')
        .select('handle')
        .eq('id', result.brandId)
        .maybeSingle();

      nav(data?.handle ? `/account/${data.handle}` : '/account/manage', { replace: true });
      setResolved(true);
    })();
  }, [user?.id, nav]);

  if (resolved) return null;
  return (
    <div className="min-h-[50vh] flex items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
};

const LegacyStoreManageRedirect: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const storeId = params.get('store_id');
    const brandId = params.get('brand_id');

    if (storeId) {
      params.delete('store_id');
      const nextQuery = params.toString();
      navigate(`/store/${storeId}${nextQuery ? `?${nextQuery}` : ''}`, { replace: true });
      return;
    }

    if (brandId) {
      navigate(`/store/create?brand_id=${brandId}`, { replace: true });
      return;
    }

    navigate('/brandstores', { replace: true });
  }, [location.search, navigate]);

  return (
    <div className="min-h-[50vh] flex items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
};

/** /store → resolve default store and redirect to /store/:storeId */
const StoreIndexRedirect: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = React.useMemo(() => {
    const sp = new URLSearchParams(window.location.search);
    return [sp] as const;
  }, []);

  React.useEffect(() => {
    if (!user?.id) return;
    (async () => {
      const { supabase } = await import('../lib/supabase');
      // Find user's brand
      const { data: membership } = await supabase
        .from('brand_users')
        .select('brand_id')
        .eq('user_id', user.id)
        .limit(1)
        .maybeSingle();
      if (!membership?.brand_id) {
        navigate('/brandstores', { replace: true });
        return;
      }
      // Resolve the main BrandStore first, with a deterministic oldest-store
      // fallback for legacy Brands that temporarily have no is_default row.
      // The lifecycle migration repairs those rows; this fallback prevents the
      // management route from abandoning the exact store before repair lands.
      const { data: store } = await supabase
        .from('brand_stores')
        .select('id')
        .eq('brand_id', membership.brand_id)
        .is('retired_at', null)
        .order('is_default', { ascending: false })
        .order('created_at', { ascending: true })
        .order('id', { ascending: true })
        .limit(1)
        .maybeSingle();
      if (store?.id) {
        const urlStoreId = searchParams.get('store_id');
        navigate(`/store/${urlStoreId || store.id}`, { replace: true });
      } else {
        navigate('/brandstores', { replace: true });
      }
    })();
  }, [user?.id, navigate, searchParams]);

  return (
    <div className="min-h-[50vh] flex items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
};

// ----------------------
// Protected Dashboard Layout
// ----------------------
const ProtectedDashboardLayout: React.FC = () => {
  return (
    <ProtectedRoute>
      <BrandStoreProvider>
        <KoordinatorI18nProvider>
          <BrandStoreLocaleExperienceSync />
          <DashboardLayout />
        </KoordinatorI18nProvider>
      </BrandStoreProvider>
    </ProtectedRoute>
  );
};

const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      // Root redirect
      {
        index: true,
        element: <RootRedirect />,
      },
      // Public routes (no sidebar)
      {
        path: 'login',
        element: <LoginPage />,
      },
      // Tailwind sanity check (safe to remove later)
      {
        path: 'tailwind-sanity',
        element: (
          <LazyPage>
            <TailwindSanityPage />
          </LazyPage>
        ),
      },
      {
        path: 'signup',
        element: <SignupPage />,
      },
      {
        path: 'auth/callback',
        element: (
          <LazyPage>
            <AuthCallbackPage />
          </LazyPage>
        ),
      },
      // Operator invite landing (public — before auth check)
      {
        path: 'invite/:token',
        element: (
          <LazyPage>
            <OperatorInvitePage />
          </LazyPage>
        ),
      },
      // Canonical brand invite acceptance route
      {
        path: 'brand/invite/:code',
        element: (
          <LazyPage>
            <OperatorInvitePage />
          </LazyPage>
        ),
      },
      {
        path: 'agent/:handle/invite/brand',
        element: (
          <LazyPage>
            <BrandHandleInvitePage inviterRole="agent" />
          </LazyPage>
        ),
      },
      {
        path: 'partner/:handle/invite/brand',
        element: (
          <LazyPage>
            <BrandHandleInvitePage inviterRole="partner" />
          </LazyPage>
        ),
      },
      // Clean handle-scoped Brand invite URLs (no /invite/ segment).
      // Doctrine: TARGET APP OWNS TARGET PAGE — the brand-specific public
      // invite page is hosted on Kloutsale. Same component, same token mint.
      // Only `agent` and `partner` are permitted brand inviters by the
      // INVITE_EDGE_MATRIX; GA/Connector are intentionally not mounted.
      {
        path: 'agent/:handle/brand',
        element: (
          <LazyPage>
            <BrandHandleInvitePage inviterRole="agent" />
          </LazyPage>
        ),
      },
      {
        path: 'partner/:handle/brand',
        element: (
          <LazyPage>
            <BrandHandleInvitePage inviterRole="partner" />
          </LazyPage>
        ),
      },
      // Neutral public role hubs on Kloutsale: multi-path invite landing pages.
      {
        path: 'agent/:handle',
        element: (
          <LazyPage>
            <BrandHandleInvitePage inviterRole="agent" variant="hub" />
          </LazyPage>
        ),
      },
      {
        path: 'partner/:handle',
        element: (
          <LazyPage>
            <BrandHandleInvitePage inviterRole="partner" variant="hub" />
          </LazyPage>
        ),
      },
      // Canonical public Profile route on Kloutsale — resolves a global member by handle.
      {
        path: 'profile/:handle',
        element: (
          <LazyPage>
            <InternalCreatorPage />
          </LazyPage>
        ),
      },
      // Plain-profile BRAND recruiting landing (Phase 1G+).
      // Must be registered alongside `profile/:handle` — order does not matter
      // because the child `/brand` segment makes the path unique.
      {
        path: 'profile/:handle/brand',
        element: (
          <LazyPage>
            <ProfileBrandInvitePage />
          </LazyPage>
        ),
      },
      // Admin portal landing (public entry — auth handled inside)
      {
        path: 'admin',
        element: <AdminLandingPage />,
      },
      // Public marketing/support pages (linked from the landing footer)
      {
        path: 'about',
        element: (
          <LazyPage>
            <AboutPage />
          </LazyPage>
        ),
      },
      {
        path: 'careers',
        element: (
          <LazyPage>
            <CareersPage />
          </LazyPage>
        ),
      },
      {
        path: 'press',
        element: (
          <LazyPage>
            <PressPage />
          </LazyPage>
        ),
      },
      {
        path: 'contact',
        element: (
          <LazyPage>
            <ContactPage />
          </LazyPage>
        ),
      },
      {
        path: 'privacy',
        element: (
          <LazyPage>
            <PublicPrivacyPage />
          </LazyPage>
        ),
      },
      {
        path: 'terms',
        element: (
          <LazyPage>
            <PublicTermsPage />
          </LazyPage>
        ),
      },
      {
        path: 'help-center',
        element: (
          <LazyPage>
            <HelpCenterPage />
          </LazyPage>
        ),
      },
      {
        path: 'api-docs',
        element: (
          <LazyPage>
            <ApiDocsPage />
          </LazyPage>
        ),
      },
      // Canonical single-step brand+store onboarding
      {
        path: 'onboarding/create-brand-store',
        element: (
          <ProtectedRoute requireOnboarding={false}>
            <LazyPage>
              <CreateBrandProfilePage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      // Phase C verification fix — canonical Profile Identity step.
      // Ensures public.members row exists before Brand/BrandStore creation
      // so attribution inheritance can stamp inviter_* fields.
      {
        path: 'onboarding/profile',
        element: (
          <ProtectedRoute requireOnboarding={false}>
            <LazyPage>
              <ProfileIdentityPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      // Legacy redirect
      {
        path: 'onboarding/create-brand-profile',
        element: <Navigate to="/onboarding/create-brand-store" replace />,
      },
      {
        path: 'onboarding/brand-assessment',
        element: (
          <ProtectedRoute requireOnboarding={false}>
            <LazyPage>
              <BrandAssessmentStep />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      // New multi-step brand onboarding wizard (Phase 1, parallel to create-brand-store)
      {
        path: 'onboarding/brand',
        element: (
          <ProtectedRoute requireOnboarding={false}>
            <LazyPage>
              <BrandOnboardingPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      // "Create another brand" entry point for already-onboarded users.
      // Uses a guided wizard (Identity → Media → Location → BrandStore handoff)
      // that reuses onboarding step components but never shows first-time
      // onboarding copy, never resumes onboarding state, and on finish
      // navigates to /store/:storeId — never to /onboarding/brand.
      {
        path: 'create/brand',
        element: (
          <ProtectedRoute requireOnboarding={false}>
            <LazyPage>
              <CreateBrandWizardPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      // Dashboard routes (with navbar + conditional sidebar)
      {
        element: <ProtectedDashboardLayout />,
        children: [
          // Command Center — cross-store operator landing page
          {
            path: 'command-center',
            element: (
              <LazyPage>
                <DashboardPage />
              </LazyPage>
            ),
          },
          // Legacy redirect: /dashboard → /command-center
          {
            path: 'dashboard',
            element: <Navigate to="/command-center" replace />,
          },
          // Instructions — support/reference surface
          {
            path: 'instructions',
            element: (
              <LazyPage>
                <BrandInstructionsPage />
              </LazyPage>
            ),
          },
          // Navbar items
          {
            path: 'home',
            element: (
              <LazyPage>
                <HomePage />
              </LazyPage>
            ),
          },
          {
            path: 'discover',
            element: (
              <LazyPage>
                <DiscoverPage />
              </LazyPage>
            ),
          },
          {
            path: 'notifications',
            element: (
              <LazyPage>
                <NotificationsPage />
              </LazyPage>
            ),
          },
          // BrandStores — SALES control surface (canonical)
          {
            path: 'brandstores',
            element: (
              <LazyPage>
                <BrandStoresPage />
              </LazyPage>
            ),
          },
          // Store surfaces (workspace view for a single store)
          {
            path: 'store',
            element: (
              <LazyPage>
                <StoreIndexRedirect />
              </LazyPage>
            ),
          },
          // Legacy redirect
          {
            path: 'stores',
            element: <Navigate to="/brandstores" replace />,
          },
          {
            path: 'store/connected',
            element: (
              <LazyPage>
                <StoreConnectedPage />
              </LazyPage>
            ),
          },
          {
            path: 'store/setup',
            element: <LegacyStoreManageRedirect />,
          },
          {
            path: 'store/assessment',
            element: (
              <LazyPage>
                <BrandAssessmentStep />
              </LazyPage>
            ),
          },
          {
            path: 'store/manage',
            element: <LegacyStoreManageRedirect />,
          },
          {
            path: 'store/clone',
            element: (
              <LazyPage>
                <CloneStorePage />
              </LazyPage>
            ),
          },
          {
            path: 'store/create',
            element: (
              <LazyPage>
                <CreateBrandStorePage />
              </LazyPage>
            ),
          },
          {
            path: 'store/:storeId/dashboard',
            element: (
              <LazyPage>
                <StoreDashboardPage />
              </LazyPage>
            ),
          },
          {
            path: 'store/:storeId',
            element: (
              <LazyPage>
                <StoreSetupPage />
              </LazyPage>
            ),
          },
          {
            path: 'proposals',
            element: (
              <LazyPage>
                <ProposalsPage />
              </LazyPage>
            ),
          },
          {
            path: 'proposals/katalog-matches',
            element: (
              <LazyPage>
                <KatalogMatchesPage />
              </LazyPage>
            ),
          },
          {
            path: 'actions',
            element: (
              <LazyPage>
                <ActionCenterPage />
              </LazyPage>
            ),
          },
          {
            path: 'proposals/create',
            element: (
              <LazyPage>
                <CreateProposalPage />
              </LazyPage>
            ),
          },
          {
            path: 'proposals/:id',
            element: (
              <LazyPage>
                <ProposalDetailPage />
              </LazyPage>
            ),
          },
          {
            path: 'proposals/:id/edit',
            element: (
              <LazyPage>
                <EditProposalPage />
              </LazyPage>
            ),
          },
          {
            path: 'products',
            element: (
              <LazyPage>
                <ProductsPage />
              </LazyPage>
            ),
          },
          {
            path: 'products/new',
            element: (
              <LazyPage>
                <ProductEditorPage />
              </LazyPage>
            ),
          },
          {
            path: 'products/:id',
            element: (
              <LazyPage>
                <ProductEditorPage />
              </LazyPage>
            ),
          },
          {
            path: 'orders',
            element: (
              <LazyPage>
                <OrdersPage />
              </LazyPage>
            ),
          },
          {
            path: 'orders/:orderId',
            element: (
              <LazyPage>
                <BrandOrderDetailPage />
              </LazyPage>
            ),
          },
          {
            path: 'fulfillment',
            element: (
              <LazyPage>
                <FulfillmentPage />
              </LazyPage>
            ),
          },
          {
            path: 'analytics',
            element: (
              <LazyPage>
                <AnalyticsPage />
              </LazyPage>
            ),
          },
          // Account section — new structure (Phase 4.3)
          {
            path: 'account/manage',
            element: (
              <LazyPage>
                <ManageBrandsPage />
              </LazyPage>
            ),
          },
          {
            path: 'account/earnings',
            element: (
              <LazyPage>
                <WalletPage />
              </LazyPage>
            ),
          },
          {
            path: 'account/earnings/withdrawals/:id',
            element: (
              <LazyPage>
                <WithdrawalStatementPage />
              </LazyPage>
            ),
          },
          {
            path: 'account/administration',
            element: (
              <LazyPage>
                <AdministrationPage />
              </LazyPage>
            ),
          },
          // Legacy redirects
          {
            path: 'account/brand',
            element: <Navigate to="/account/administration" replace />,
          },
          {
            path: 'account/wallet',
            element: <Navigate to="/account/earnings" replace />,
          },
          {
            path: 'account/brand-profile',
            element: <Navigate to="/account/manage" replace />,
          },
          {
            path: 'account/brand-store',
            element: <BrandStoreRedirect />,
          },
          {
            path: 'account/brand/info',
            element: (
              <LazyPage>
                <BrandInfoPage />
              </LazyPage>
            ),
          },
          {
            path: 'account/team',
            element: (
              <LazyPage>
                <TeamMembersPage />
              </LazyPage>
            ),
          },
          {
            path: 'account/agent',
            element: (
              <LazyPage>
                <BrandAgentPage />
              </LazyPage>
            ),
          },
          {
            path: 'account/verify-brand',
            element: (
              <LazyPage>
                <VerifyBrandPage />
              </LazyPage>
            ),
          },
          {
            path: 'account/verify-owner',
            element: (
              <LazyPage>
                <VerifyOwnerPage />
              </LazyPage>
            ),
          },
          // Brand-scoped management route — /account/:handle resolves to brand identity surface
          // MUST come LAST after all specific /account/* routes to avoid swallowing them
          {
            path: 'account/:handle',
            element: (
              <LazyPage>
                <BrandStorePage />
              </LazyPage>
            ),
          },
          {
            path: 'account/terms',
            element: (
              <LazyPage>
                <TermsPage />
              </LazyPage>
            ),
          },
          {
            path: 'account/wallet/add',
            element: (
              <LazyPage>
                <AddFundsPage />
              </LazyPage>
            ),
          },
          {
            path: 'account/wallet/balance',
            element: (
              <LazyPage>
                <WalletBalancePage />
              </LazyPage>
            ),
          },
          // Admin routes
          {
            path: 'admin/invite',
            element: (
              <LazyPage>
                <InvitePage />
              </LazyPage>
            ),
          },
          // Account settings (personal: email, password, 2FA, sessions, privacy)
          {
            path: 'account/settings',
            element: (
              <LazyPage>
                <SettingsPage />
              </LazyPage>
            ),
          },
          // Legacy redirects
          {
            path: 'invite',
            element: <Navigate to="/admin/invite" replace />,
          },
          {
            path: 'settings',
            element: <Navigate to="/account/settings" replace />,
          },
          {
            path: 'admin/settings',
            element: <Navigate to="/account/settings" replace />,
          },
          // Agent Program
          {
            path: 'account/agent-apply',
            element: (
              <LazyPage>
                <BrandAgentApplyPage />
              </LazyPage>
            ),
          },
          // Kloutforce Information
          {
            path: 'kloutforce-info',
            element: (
              <LazyPage>
                <KloutforceInfoPage />
              </LazyPage>
            ),
          },
          // Brand Instructions
          {
            path: 'instructions',
            element: (
              <LazyPage>
                <BrandInstructionsPage />
              </LazyPage>
            ),
          },
          // Creator Discovery
          {
            path: 'creators',
            element: (
              <LazyPage>
                <CreatorDiscoveryPage />
              </LazyPage>
            ),
          },
          // Internal brand store views (within dashboard layout)
          {
            path: 'brand/:handle/:slug',
            element: (
              <LazyPage>
                <InternalBrandStorePage />
              </LazyPage>
            ),
          },
          {
            path: 'brand/:handle',
            element: (
              <LazyPage>
                <InternalBrandStorePage />
              </LazyPage>
            ),
          },
          // Creator handle catch-all (MUST be last in dashboard children)
          {
            path: ':handle',
            element: (
              <LazyPage>
                <InternalCreatorPage />
              </LazyPage>
            ),
          },
        ],
      },
      // 404 fallback
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
]);

export { router };
export default router;
